import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store';
import { translations } from '../translations';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, language, darkMode, toggleLanguage, toggleTheme } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  
  const t = translations[language];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: t.dashboard, icon: '🏠' },
    { path: '/rewards', label: t.myRewards, icon: '🎁' },
    { path: '/challenges', label: t.challenges, icon: '🏆' },
    { path: '/transactions', label: t.history, icon: '📋' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className={`flex bg-gray-100 min-h-screen relative dark:bg-gray-900 transition-colors duration-200`}>
      {/* Mobile Top Header */}
      <div className="lg:hidden fixed top-0 w-full bg-white dark:bg-gray-800 shadow-sm z-30 px-4 py-3 flex justify-between items-center h-16 transition-colors">
        <div className="flex items-center gap-2">
           {/* Burger Menu Button */}
          <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 dark:text-gray-300 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img 
            src="/caldea-logo.png" 
            alt="Caldea" 
            className="h-6 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <div className="flex items-center gap-2">
            <span className="text-cyan-600 font-bold text-sm dark:text-cyan-400">{t.level} {user?.improvementLevel || 125}</span>
        </div>
      </div>

      {/* Overlay for Mobile Sidebar */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar (Drawer on Mobile, Static on Desktop) */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col h-full lg:h-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center h-20 lg:h-auto">
          <div className="flex items-center gap-3">
            <img 
              src="/caldea-logo.png" 
              alt="Caldea" 
              className="h-8 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1 className="text-xl font-bold text-cyan-600 dark:text-cyan-400">Caldea Club</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.poweredBy}</p>
            </div>
          </div>
          {/* Close Button Mobile */}
          <button 
            onClick={() => setIsOpen(false)}
            className="lg:hidden text-gray-400 hover:text-gray-600 p-2"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 py-6 overflow-y-auto">
          {navItems.map((item) => (
            <a 
              key={item.path}
              href={item.path} 
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3 px-6 py-3 transition ${
                isActive(item.path) 
                  ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border-r-4 border-cyan-600 font-semibold' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              {item.label}
            </a>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t dark:border-gray-700 bg-gray-50 lg:bg-white dark:bg-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center text-xl">
              👩
            </div>
            <div>
              <p className="font-semibold text-sm dark:text-white">María García</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Club Premium</p>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <button 
                onClick={toggleTheme}
                className="flex-1 py-1 text-xs border rounded hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
                {darkMode ? '☀️ Light' : '🌙 Dark'}
            </button>
            <button 
                onClick={toggleLanguage}
                className="flex-1 py-1 text-xs border rounded hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
                {language === 'es' ? '🇺🇸 EN' : '🇪🇸 ES'}
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-white lg:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 text-gray-600 rounded-lg border border-gray-200 dark:border-gray-600 lg:border-transparent hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition text-sm font-medium"
          >
            {t.logout}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto h-screen pt-16 lg:pt-0">
        {/* Desktop Header (Hidden on Mobile) */}
        <header className="hidden lg:block bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10 transition-colors">
          <div className="px-8 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                {navItems.find(item => item.path === location.pathname)?.label || 'Caldea Club'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
                <div className="flex gap-2 mr-4">
                    <button onClick={toggleTheme} className="text-xl" title="Toggle Theme">
                        {darkMode ? '☀️' : '🌙'}
                    </button>
                    <button onClick={toggleLanguage} className="text-sm font-bold bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded dark:text-white">
                        {language.toUpperCase()}
                    </button>
                </div>
              <div className="flex items-center gap-2 bg-cyan-50 dark:bg-cyan-900/40 px-4 py-2 rounded-full">
                <span className="text-cyan-600 dark:text-cyan-400 font-bold">{user?.improvementLevel || 125}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t.level}</span>
              </div>
              <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/40 px-4 py-2 rounded-full">
                <span className="text-orange-600 dark:text-orange-400">🔥</span>
                <span className="text-sm font-semibold dark:text-gray-200">8 {t.days}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
